/**
 * The Thunder Energy mark — the company monogram: a lightning bolt on a gradient disc.
 *
 * Replaces the old "Aperture L" Lumen mark (Logo Lumen/lumen-all-concepts/2-aperture-l).
 * This swaps the LOGO ARTWORK only. Thunder is both the product and the company; the mark is Thunder Energy's
 * that builds it, so the "Thunder" wordmark text standing beside this mark in Shell.tsx and
 * on the sign-in screen is untouched.
 *
 * NOTE: Docs/06-design/BRAND.md still specifies the Aperture L concept and its amber tile
 * palette, and is stale as of this change. It lives outside this repo, so it has not been
 * updated here.
 *
 * WHY THE CIRCLE VARIANT, AND WHY THE SAME ONE IN EVERY IN-APP SLOT
 *
 * The mark is a SELF-CONTAINED TILE: it carries its own orange->gold ground, so the only
 * contrast that matters is the #2C2C2C bolt against that gradient, and that pairing is
 * identical in light, dark and system-default. A theme-paired or `currentColor` mark would
 * be three code paths to keep correct for zero visual gain — and the artwork cannot do
 * `currentColor` anyway, because the bolt is a solid fill inside a gradient rect.
 *
 * The SQUARE variant (thunder-mark-square.svg) is reserved for public/favicon-180.png,
 * where iOS applies its own squircle mask and a pre-rounded source would be double-rounded.
 *
 * WHY `<img>` RATHER THAN INLINE SVG
 *
 * The file is ~1.1KB, well under Vite's 4KB `assetsInlineLimit`, so it is emitted as a
 * data URI and costs no network fetch — which is the reason the old mark was inlined by
 * hand. It also means two copies on one page cannot collide on the artwork's gradient id,
 * which the hand-inlined version could.
 */
import type { ReactNode } from 'react';
import markUrl from '@/assets/brand/thunder-mark-circle.svg';

export function BrandMark({ size = 34 }: { size?: number }): ReactNode {
  return (
    <img
      src={markUrl}
      width={size}
      height={size}
      /* `block` kills the inline-image baseline gap, which otherwise pushes the mark a
         couple of pixels below the wordmark it is aligned with in the 64px header. */
      style={{ flex: 'none', display: 'block' }}
      /*
       * Names what is DRAWN. The old label said "Thunder", which described the product
       * rather than the artwork; this mark is the company's, and in the header a screen
       * reader reads it immediately before the "Thunder" wordmark text beside it.
       */
      alt="Thunder Energy"
    />
  );
}
